import { Receipt as ReceiptIcon } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { every, filter, isEmpty, isObject, keys, sortBy, union } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReceiptValidationType } from 'enums';
import { useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Order } from 'types';

import { ReceiptsValidationDetailsDialog } from './ReceiptsValidationDetailsDialog';
import { ReceiptsValidationSummary } from './ReceiptsValidationSummary';

const TECH_PROTECTION_VALIDATION_TYPES = [
  ReceiptValidationType.TechProtectionMatch,
  ReceiptValidationType.TechProtectionEdited,
  ReceiptValidationType.TechProtectionRiskScore,
  ReceiptValidationType.TechProtectionVerdict,
];

const CHEQUE_WALES_VALIDATION_TYPES = [
  ReceiptValidationType.ChequeWalesVerdict,
  ReceiptValidationType.ChequeWalesDate,
  ReceiptValidationType.ChequeWalesAmount,
  ReceiptValidationType.ChequeWalesBank,
  ReceiptValidationType.ChequeWalesRecipient,
];

type Props = {
  order: Order;
  labelled?: boolean;
};

export const OrderReceiptsValidation: React.FC<Props> = ({
  order,
  labelled,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.receipt_validation',
  });
  const { isManager } = useUser();
  const [detailsOpen, setDetailsOpen] = useState(false);

  const showReceiptsValidation = useMemo(
    () => !isEmpty(order.receiptsValidation) && isManager,
    [isManager, order.receiptsValidation],
  );

  const validationSuccess = useMemo(
    () =>
      every(
        filter(
          order.receiptsValidation,
          (validation) =>
            validation.type !== ReceiptValidationType.TechProtectionRiskScore,
        ),
        (validation) => validation.success,
      ),
    [order.receiptsValidation],
  );

  const templateValidations = useMemo(
    () =>
      filter(order.receiptsValidation, (validation) =>
        [
          ReceiptValidationType.Amount,
          ReceiptValidationType.Date,
          ReceiptValidationType.Phone,
          ReceiptValidationType.CardLastDigits,
        ].includes(validation.type),
      ),
    [order.receiptsValidation],
  );

  const stopScamValidations = useMemo(
    () =>
      sortBy(
        filter(order.receiptsValidation, (validation) =>
          [
            ReceiptValidationType.StopScamAmount,
            ReceiptValidationType.StopScamDate,
            ReceiptValidationType.StopScamValid,
          ].includes(validation.type),
        ),
        (validation) => validation.type === ReceiptValidationType.StopScamValid,
      ),
    [order.receiptsValidation],
  );

  const techProtectionValidations = useMemo(
    () =>
      filter(order.receiptsValidation, (validation) =>
        TECH_PROTECTION_VALIDATION_TYPES.includes(validation.type),
      ),
    [order.receiptsValidation],
  );

  const chequeWalesValidations = useMemo(
    () =>
      filter(order.receiptsValidation, (validation) =>
        CHEQUE_WALES_VALIDATION_TYPES.includes(validation.type),
      ),
    [order.receiptsValidation],
  );

  const allFilenames = useMemo(() => {
    let filenames: string[] = [];
    const allValidations = [
      ...stopScamValidations,
      ...techProtectionValidations,
      ...chequeWalesValidations,
    ];
    for (const validation of allValidations) {
      if (isObject(validation.details)) {
        filenames = union(filenames, keys(validation.details));
      }
    }
    return filenames;
  }, [stopScamValidations, techProtectionValidations, chequeWalesValidations]);

  const hasPerFileDetails = allFilenames.length > 0;

  const handleIconClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setDetailsOpen(true);
  }, []);

  const handleDetailsClose = useCallback(() => {
    setDetailsOpen(false);
  }, []);

  const renderTooltipTitle = useCallback(
    () => (
      <Fragment>
        <div className="tw-mb-2 tw-text-sm">{t('title')}</div>
        <ReceiptsValidationSummary
          templateValidations={templateValidations}
          stopScamValidations={stopScamValidations}
          techProtectionValidations={techProtectionValidations}
          chequeWalesValidations={chequeWalesValidations}
        />
        {hasPerFileDetails && (
          <div className="tw-text-xs tw-mt-2 tw-opacity-70 tw-italic">
            {t('click_for_details')}
          </div>
        )}
      </Fragment>
    ),
    [
      t,
      templateValidations,
      stopScamValidations,
      techProtectionValidations,
      chequeWalesValidations,
      hasPerFileDetails,
    ],
  );

  return (
    <Fragment>
      {showReceiptsValidation && (
        <div className="tw-flex tw-items-center">
          {labelled && (
            <div>
              {validationSuccess ? t('status.success') : t('status.failed')}
            </div>
          )}
          <Tooltip title={renderTooltipTitle()}>
            <ReceiptIcon
              fontSize="small"
              sx={{
                ml: labelled ? 1 : 0,
                cursor: hasPerFileDetails ? 'pointer' : 'default',
              }}
              color={validationSuccess ? 'success' : 'error'}
              onClick={handleIconClick}
            />
          </Tooltip>
        </div>
      )}
      <ReceiptsValidationDetailsDialog
        open={detailsOpen}
        filenames={allFilenames}
        templateValidations={templateValidations}
        stopScamValidations={stopScamValidations}
        techProtectionValidations={techProtectionValidations}
        chequeWalesValidations={chequeWalesValidations}
        onClose={handleDetailsClose}
      />
    </Fragment>
  );
};
